import { atom } from 'jotai';

import { BaseNode, Category, GraphLineNode, GraphsNode, ParameterNode, Relation, SwingMeasuredParameter } from '@data';
/**
 * UI Node Parameters
 */
export const parametersAtom = atom<ParameterNode[]>();
export const graphsAtom = atom<GraphsNode[]>();
export const graphLinesAtom = atom<GraphLineNode[]>();

export const parametersMapAtom = atom((get) => {
    const parameters = get(parametersAtom);
    return Object.fromEntries(parameters?.map((param) => [param.id, param] as const) || []);
});

export const uiNodeBySmpIdAtom = atom((get) => {
    const parameters = get(parametersAtom);
    return Object.fromEntries(parameters?.map((param) => [param.parameter.smpId, param] as const) || []);
});

export const parametersByGroupAtom = atom((get) => {
    const parameters = get(parametersAtom);
    return parameters?.reduce(
        (acc, param) => {
            const groupId = param.categories.swingFoundationGroup;
            if (!groupId) return acc;
            if (!acc[groupId]) acc[groupId] = [];
            acc[groupId].push(param);
            return acc;
        },
        {} as Record<string, ParameterNode[]>,
    );
});

export const uiNodesAtom = atom<BaseNode[]>();
export const uiGraphLineNodesAtom = atom<GraphLineNode[]>((get) => {
    return get(graphLinesAtom) ?? [];
});

export const parametersByTimeSeriesAndAxisAtom = atom((get) =>
    (get(parametersAtom) ?? []).reduce(
        (acc, parameter) => {
            const key = `${parameter.parameter.timeSeriesId}:${parameter.parameter.axis ?? 'x'}`;
            if (!Array.isArray(acc[key])) {
                acc[key] = [];
            }
            acc[key].push(parameter);
            return acc;
        },
        {} as Record<string, ParameterNode[]>,
    ),
);
export const parametersByGraphLineAtom = atom((get) => {
    const parametersByTimeSeriesAndAxis = get(parametersByTimeSeriesAndAxisAtom);

    return Object.fromEntries(
        (get(uiGraphLineNodesAtom) ?? []).map((graphLine) => [
            graphLine.id,
            parametersByTimeSeriesAndAxis[`${graphLine.timeSeries.id}:${graphLine.axis ?? 'x'}`] ?? [],
        ]),
    );
});

export const uiNodesMapAtom = atom((get) => {
    const nodes = get(uiNodesAtom);
    return Object.fromEntries(nodes?.map((node) => [node.id, node] as const) || []);
});

export const relationsAtom = atom<Relation[]>();
export const relationsByParentMapAtom = atom((get) => {
    const relations = get(relationsAtom);
    return (
        relations?.reduce(
            (acc, relation) => {
                if (!acc[relation.parentUiNodeId]) acc[relation.parentUiNodeId] = [];
                acc[relation.parentUiNodeId].push(relation);
                return acc;
            },
            {} as Record<string, Relation[]>,
        ) || {}
    );
});

export const graphLinesByParentAtom = atom((get) => {
    const relationsByParent = get(relationsByParentMapAtom);
    const graphLines = get(uiGraphLineNodesAtom);

    return Object.fromEntries(
        Object.entries(relationsByParent).map(([parentId, relations]) => {
            const childIds = new Set(relations.map(({ childUiNodeId }) => childUiNodeId));
            const lines = graphLines.filter((line) => line.showOn.includes('kiosk') && childIds.has(line.id));

            return [parentId, lines] as const;
        }),
    );
});

export const categoriesAtom = atom<Category[]>();

export const swingFoundationCategoriesAtom = atom<Category[]>((get) => {
    const categories = get(categoriesAtom);
    return categories?.filter((category) => category.type === 'swing_foundation_group') ?? [];
});

export const P_POSITION_REGEX = /p\d{1,2}(?=_|$)/;

/**
 * Swing foundation parameter groups for each position
 * ```
 * {
 *      ["p1"| "p2" | ...]: Category[]
 * }
 * ```
 */
export const swingFoundationCategoriesByPositionAtom = atom((get) => {
    const swingFoundationCategories = get(swingFoundationCategoriesAtom);
    return swingFoundationCategories.reduce(
        (acc, category) => {
            const id = category.id;
            const position = id.match(P_POSITION_REGEX)?.[0];
            if (!position) return acc;
            if (!acc[position]) acc[position] = [];
            acc[position].push(category);
            return acc;
        },
        {} as Record<string, Category[]>,
    );
});

/**
 * Swing foundation parameter groups for each position
 * ```
 * {
 *      ["p1"| "p2" | ...]: {
 *          [categoryId: string]: ParamtererNode[]
 *      }
 * }
 * ```
 */
export const swingFoundationGroupedParametersAtom = atom((get) => {
    const parametersByGroup = get(parametersByGroupAtom);
    if (!parametersByGroup) return {};
    const categoriesByPosition = get(swingFoundationCategoriesByPositionAtom);
    const categoryPositions = Object.keys(categoriesByPosition);

    return categoryPositions.reduce(
        (acc, position) => {
            acc[position] = categoriesByPosition[position].reduce(
                (innerAcc, category) => {
                    const parameters = parametersByGroup[category.id];
                    if (!parameters?.length) return innerAcc;
                    innerAcc[category.id] = parametersByGroup[category.id];
                    return innerAcc;
                },
                {} as Record<string, ParameterNode[]>,
            );
            return acc;
        },
        {} as Record<string, Record<string, ParameterNode[]>>,
    );
});

export const swingMeasuredParametersAtom = atom<SwingMeasuredParameter[]>();
export const swingMeasuredParametersMapAtom = atom((get) => {
    const swingMeasuredParameters = get(swingMeasuredParametersAtom);
    return Object.fromEntries(swingMeasuredParameters?.map((smp) => [smp.id, smp] as const) || []);
});
